import type { MediaVariantsType } from '@/services/strapiSchema';

const FALLBACK_MAX_WIDTH = 800;
const SKIP_MIMES = new Set(['image/svg+xml', 'image/gif']);

export interface MediaFormatLike {
  url: string;
  width: number;
  height: number;
  mime: string;
}

export interface MediaLike {
  url: string;
  width?: number | null;
  height?: number | null;
  mime?: string;
  formats?: Record<string, MediaFormatLike | null | undefined> | null;
}

const mimeFromUrl = (url: string): string | undefined => {
  try {
    const ext = new URL(url).pathname.split('.').pop()?.toLowerCase();
    if (ext === 'svg') return 'image/svg+xml';
    if (ext === 'gif') return 'image/gif';
    if (ext === 'webp') return 'image/webp';
    if (ext === 'avif') return 'image/avif';
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    if (ext === 'png') return 'image/png';
  } catch {
    return undefined;
  }
  return undefined;
};

const isUsableFormat = (format: MediaFormatLike | null | undefined): format is MediaFormatLike => (
  format !== null
  && format !== undefined
  && typeof format.url === 'string'
  && Number.isFinite(format.width)
  && format.width > 0
  && Number.isFinite(format.height)
  && typeof format.mime === 'string'
);

const isWebp = (mime: string, url: string) => (
  mime === 'image/webp' || url.split('?')[0].toLowerCase().endsWith('.webp')
);

/**
 * Construit les variantes responsive à partir des `formats` Strapi.
 * `thumbnail` est ignoré ; l'original n'entre dans le srcset que s'il est déjà en WebP.
 */
export function toMediaVariants(media: MediaLike): MediaVariantsType | null {
  const mime = (media.mime ?? mimeFromUrl(media.url))?.toLowerCase();
  if (mime && SKIP_MIMES.has(mime)) return null;

  const { formats } = media;
  if (!formats || Object.keys(formats).length === 0) return null;

  const candidates: MediaFormatLike[] = Object.entries(formats)
    .filter(([key, format]) => key.startsWith('w') && isUsableFormat(format))
    .map(([, format]) => format as MediaFormatLike)
    .filter((format) => isWebp(format.mime, format.url));

  if (
    typeof media.width === 'number'
    && media.width > 0
    && typeof media.height === 'number'
    && mime
    && isWebp(mime, media.url)
    && !candidates.some((format) => format.width === media.width)
  ) {
    candidates.push({
      url: media.url,
      width: media.width,
      height: media.height,
      mime,
    });
  }

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => a.width - b.width);

  const src = candidates.reduce((best, format) => (
    Math.abs(format.width - FALLBACK_MAX_WIDTH) < Math.abs(best.width - FALLBACK_MAX_WIDTH)
      ? format
      : best
  ));
  const largest = candidates[candidates.length - 1];

  return {
    width: largest.width,
    height: largest.height,
    src: src.url,
    webp: candidates.map((format) => `${format.url} ${format.width}w`).join(', '),
  };
}
